/**
 * Emergent Marketplace — Phase 2
 * CartDrawer.jsx
 * Architectural Layer: Presentation → Cart
 *
 * Slide-out cart panel. Groups items by vendor. Shows per-vendor subtotals.
 * Integrates Stripe Elements for secure checkout.
 * Design: Clean ivory + deep teal — grounded, trustworthy commerce.
 * Font: Fraunces (display) + DM Sans (UI)
 */

import { useState, useEffect } from "react";
import { X, ShoppingBag, Trash2, Plus, Minus, Store, ChevronRight, Lock, CreditCard, Check, AlertCircle, Loader, MapPin, Truck } from "lucide-react";
import { apiFetch } from "./api";
import MarketplaceCardForm from "./MarketplaceCardForm";
import { isMobileBuild } from "@/lib/mobileBuild";
import UpgradeOnWebNotice from "@/components/UpgradeOnWebNotice";
import { useTaxEnabled, billingAddressFor, BillingZipField } from "@/components/BillingZip";

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

  .cart-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    z-index: 200;
    animation: cart-fadein 0.2s ease;
    backdrop-filter: blur(3px);
  }

  @keyframes cart-fadein { from { opacity: 0; } to { opacity: 1; } }

  .cart-drawer.is-page {
    position: relative;
    inset: auto;
    /* The drawer sits at 201 to clear the page; as a page card it must pass UNDER the sticky
       Marketplace header instead of scrolling over the shelf pills. */
    z-index: auto;
    width: 100%;
    max-width: 720px;
    height: auto;
    min-height: 0;
    margin: 0 auto;
    border-radius: 16px;
    border: 1px solid #e8e0d4;
    box-shadow: none;
    animation: none;
    overflow: hidden;
  }
  /* flex:1 made the list grow past its lines and leave a blank band underneath */
  .cart-drawer.is-page .cart-body { max-height: none; flex: none; }

  .cart-drawer {
    position: fixed;
    top: 0; right: 0;
    width: 100%;
    max-width: 420px;
    height: 100vh;
    background: #faf8f4;
    z-index: 201;
    display: flex;
    flex-direction: column;
    animation: cart-slidein 0.3s cubic-bezier(0.32, 0, 0.15, 1);
    box-shadow: -12px 0 48px rgba(0,0,0,0.16);
    font-family: 'DM Sans', sans-serif;
  }

  @keyframes cart-slidein {
    from { transform: translateX(100%); }
    to   { transform: translateX(0); }
  }

  /* Header */
  .cart-header {
    padding: 20px 20px 16px;
    border-bottom: 1px solid #e8e0d4;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #ffffff;
    flex-shrink: 0;
  }

  .cart-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .cart-title {
    font-family: 'Fraunces', serif;
    font-size: 20px;
    font-weight: 600;
    color: #1a2e2a;
    margin: 0;
  }

  .cart-count {
    background: #1a4a3a;
    color: white;
    border-radius: 12px;
    padding: 2px 9px;
    font-size: 12px;
    font-weight: 600;
  }

  .cart-close {
    width: 34px; height: 34px;
    border-radius: 50%;
    border: 1px solid #e8e0d4;
    background: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #7a7060;
    transition: background 0.15s;
  }

  .cart-close:hover { background: #f0ebe0; }

  /* Body */
  .cart-body {
    flex: 1;
    overflow-y: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .cart-body::-webkit-scrollbar { display: none; }

  /* Vendor group */
  .cart-vendor-group {
    margin-bottom: 0;
    border-bottom: 4px solid #f0ebe0;
  }

  .cart-vendor-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 20px 10px;
    background: #f7f4ef;
    border-bottom: 1px solid #e8e0d4;
  }

  .cart-vendor-avatar {
    width: 28px; height: 28px;
    border-radius: 7px;
    background: linear-gradient(135deg, #2d6b50, #1a4a3a);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Fraunces', serif;
    font-size: 12px;
    color: white;
    font-weight: 600;
    flex-shrink: 0;
  }

  .cart-vendor-name {
    font-size: 12px;
    font-weight: 600;
    color: #3a3028;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .cart-vendor-subtotal {
    margin-left: auto;
    font-size: 13px;
    font-weight: 600;
    color: #1a4a3a;
  }

  /* Cart item */
  .cart-item {
    display: flex;
    gap: 14px;
    padding: 16px 20px;
    border-bottom: 1px solid #f0ebe0;
    background: white;
    animation: cart-item-in 0.2s ease;
  }

  @keyframes cart-item-in {
    from { opacity: 0; transform: translateX(8px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .cart-item-thumb {
    width: 60px; height: 60px;
    border-radius: 10px;
    background: linear-gradient(135deg, #e8e0d0, #d8cfc0);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #b0a090;
  }

  .cart-item-info { flex: 1; min-width: 0; }

  .cart-item-type {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #8a7a6a;
    margin-bottom: 3px;
  }

  .cart-item-name {
    font-family: 'Fraunces', serif;
    font-size: 15px;
    font-weight: 400;
    color: #1a2e2a;
    margin: 0 0 8px;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cart-item-controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .cart-qty-row {
    display: flex;
    align-items: center;
    gap: 0;
    background: #f0ebe0;
    border-radius: 8px;
    overflow: hidden;
  }

  .cart-qty-btn {
    width: 30px; height: 30px;
    border: none;
    background: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #4a4038;
    transition: background 0.1s;
  }

  .cart-qty-btn:hover { background: #e0d8cc; }
  .cart-qty-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .cart-qty-val {
    min-width: 32px;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: #1a2e2a;
  }

  .cart-item-price {
    font-size: 15px;
    font-weight: 600;
    color: #1a2e2a;
  }

  .cart-remove-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #c0a090;
    padding: 4px;
    transition: color 0.15s;
  }

  .cart-remove-btn:hover { color: #c0392b; }

  /* Empty cart */
  .cart-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 32px;
    text-align: center;
    flex: 1;
  }

  .cart-empty-icon {
    width: 72px; height: 72px;
    border-radius: 50%;
    background: #f0ebe0;
    border: 1px solid #e8e0d4;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    color: #b0a090;
  }

  .cart-empty h3 {
    font-family: 'Fraunces', serif;
    font-size: 22px;
    font-weight: 400;
    font-style: italic;
    color: #1a2e2a;
    margin: 0 0 8px;
  }

  .cart-empty p { font-size: 14px; color: #8a7a6a; margin: 0; }

  /* Footer / Summary */
  .cart-footer {
    background: white;
    border-top: 1px solid #e8e0d4;
    flex-shrink: 0;
  }

  .cart-summary {
    padding: 16px 20px 12px;
  }

  .cart-summary-row {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    color: #6a6058;
    padding: 4px 0;
  }

  .cart-summary-total {
    display: flex;
    justify-content: space-between;
    font-size: 17px;
    font-weight: 600;
    color: #1a2e2a;
    padding: 10px 0 4px;
    border-top: 1px solid #e8e0d4;
    margin-top: 6px;
  }

  .cart-summary-total-label { font-family: 'Fraunces', serif; }

  .cart-checkout-btn {
    width: calc(100% - 40px);
    margin: 0 20px 20px;
    padding: 15px;
    border-radius: 12px;
    background: #1a4a3a;
    border: none;
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: background 0.15s;
  }

  .cart-checkout-btn:hover { background: #2d6b50; }
  .cart-checkout-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .cart-secure-note {
    display: flex;
    align-items: center;
    gap: 6px;
    justify-content: center;
    font-size: 11px;
    color: #a09080;
    padding: 0 20px 16px;
  }

  /* ─── Checkout step ───────────────────────────────────────── */
  .cart-checkout-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .cart-checkout-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 20px;
    border-bottom: 1px solid #e8e0d4;
    flex-shrink: 0;
  }

  .cart-back-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #7a7060;
    padding: 4px;
    display: flex;
    align-items: center;
  }

  .cart-checkout-title {
    font-family: 'Fraunces', serif;
    font-size: 19px;
    font-weight: 600;
    color: #1a2e2a;
    margin: 0;
  }

  .cart-checkout-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px 20px;
  }

  .co-section-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #8a7a6a;
    margin: 0 0 12px;
  }

  .co-field { margin-bottom: 14px; }

  .co-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #6a6058;
    margin-bottom: 6px;
  }

  .co-input {
    width: 100%;
    background: #faf8f4;
    border: 1.5px solid #e0d8cc;
    border-radius: 9px;
    padding: 11px 14px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    color: #1a2e2a;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s;
  }

  .co-input:focus { border-color: #1a4a3a; background: white; }

  .co-row { display: flex; gap: 10px; }
  .co-row .co-field { flex: 1; }

  .co-divider {
    height: 1px;
    background: #e8e0d4;
    margin: 20px 0;
  }

  /* Stripe card element placeholder */
  .co-stripe-element {
    background: #faf8f4;
    border: 1.5px solid #e0d8cc;
    border-radius: 9px;
    padding: 13px 14px;
    margin-bottom: 8px;
    font-size: 14px;
    color: #8a7a6a;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* Order summary mini */
  .co-order-summary {
    background: #f7f4ef;
    border: 1px solid #e8e0d4;
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 20px;
  }

  .co-order-row {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    padding: 3px 0;
    color: #6a6058;
  }

  .co-order-total {
    display: flex;
    justify-content: space-between;
    font-size: 15px;
    font-weight: 700;
    color: #1a2e2a;
    padding-top: 8px;
    margin-top: 6px;
    border-top: 1px solid #e0d8cc;
  }

  .co-pay-btn {
    width: 100%;
    padding: 15px;
    border-radius: 12px;
    background: #1a4a3a;
    border: none;
    color: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: background 0.15s;
    margin-bottom: 12px;
  }

  .co-pay-btn:hover { background: #2d6b50; }
  .co-pay-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Success state */
  .co-success {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 40px 28px;
    flex: 1;
  }

  .co-success-circle {
    width: 80px; height: 80px;
    border-radius: 50%;
    background: #eef5f1;
    border: 2px solid #b8d6c3;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
    color: #2d6b4a;
  }

  .co-success-title {
    font-family: 'Fraunces', serif;
    font-size: 26px;
    font-weight: 400;
    font-style: italic;
    color: #1a2e2a;
    margin: 0 0 10px;
  }

  .co-success-body { font-size: 14px; color: #6a6058; line-height: 1.6; margin: 0 0 28px; }

  .co-order-ref {
    background: #f0ebe0;
    border: 1px solid #e0d8cc;
    border-radius: 10px;
    padding: 12px 20px;
    font-size: 13px;
    color: #4a4038;
    margin-bottom: 24px;
    width: 100%;
  }

  .co-order-ref strong { display: block; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: #8a7a6a; margin-bottom: 4px; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MOCK_CART = {
  id: "cart1",
  user_id: "user1",
  items: [
    { product_id: "1", vendor_id: "v1", vendor_name: "Sacred Roots", qty: 1, price_snapshot: 27,  type: "digital",  title: "Morning Awakening Series" },
    { product_id: "2", vendor_id: "v1", vendor_name: "Sacred Roots", qty: 2, price_snapshot: 48,  type: "physical", title: "Selenite Charging Bowl" },
    { product_id: "4", vendor_id: "v2", vendor_name: "The Still Point", qty: 1, price_snapshot: 120, type: "session", title: "1:1 Clarity Session (60 min)" },
  ],
};

const groupByVendor = (items) => {
  const groups = {};
  for (const item of items) {
    if (!groups[item.vendor_id]) {
      groups[item.vendor_id] = { vendor_id: item.vendor_id, vendor_name: item.vendor_name || "Shop", items: [] };
    }
    groups[item.vendor_id].items.push(item);
  }
  return Object.values(groups);
};

const TYPE_ICONS = {  digital: "↓", physical: "📦", course: "📖", session: "🗓️",
};

// Two lines of the same product differ only by type, so every id carries it
const vkey = (i) => (i.variant_name
  ? `-${i.variant_name.toLowerCase().replace(/\s+/g, '-')}` : '');

// ─── Checkout Panel ───────────────────────────────────────────────────────────

function CheckoutPanel({ cart, total, onBack, onSuccess, onDismiss }) {
  const [step, setStep] = useState("address"); // address | payment | success
  const [address, setAddress] = useState({ name: "", line1: "", line2: "", city: "", state: "", zip: "", country: "US" });
  const [processing, setProcessing] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [orderNumber, setOrderNumber] = useState(null);
  const [intent, setIntent] = useState(null);   // { client_secret, payment_intent_id, order_id, amount_tax }
  const [error, setError] = useState("");
  const needsShipping = cart.items.some(i => i.type === "physical");
  const taxEnabled = useTaxEnabled();
  const [billingZip, setBillingZip] = useState("");

  const upd = (f) => setAddress(a => ({ ...a, ...f }));

  const startPayment = async () => {
    if (needsShipping) {
      const missing = ["name", "line1", "city", "state", "zip"].filter(f => !address[f]?.trim());
      if (missing.length) { setError("Please complete your shipping address."); return; }
    } else if (taxEnabled && billingZip.trim().length < 5) {
      setError("Please enter your billing ZIP code so we can calculate sales tax.");
      return;
    }
    setProcessing(true); setError("");
    try {
      const result = await apiFetch("/cart/checkout", {
        method: "POST",
        body: JSON.stringify({
          shipping_address: needsShipping ? address : null,
          billing_address: needsShipping
            ? null
            : (billingAddressFor(billingZip) ? { name: "", line1: "", city: "", state: "", zip: billingZip.trim(), country: "US" } : null),
        }),
      });
      setIntent(result);
      setOrderId(result.order_id);
      setStep("payment");
    } catch (e) {
      setError(e.message || "We couldn't start your payment. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handlePaid = (res) => {
    setOrderNumber(res?.order_number || null);
    setStep("success");
  };

  if (step === "success") {
    return (
      <div className="co-success" style={{ position: "relative" }}>
        {/* The confirmation used to offer one way out — straight to My Orders. Nobody should be
            pinned to a screen they have finished with (owner, Jun 2026). */}
        <button
          onClick={onDismiss}
          aria-label="Close"
          style={{
            position: "absolute", top: 0, right: 0, background: "none", border: "none",
            color: "#8a8078", cursor: "pointer", padding: 6, lineHeight: 0,
          }}
          data-testid="checkout-success-close-btn"
        >
          <X size={18} />
        </button>
        <div className="co-success-circle"><Check size={36} strokeWidth={2.5} /></div>
        <h3 className="co-success-title">Order confirmed</h3>
        <p className="co-success-body">
          Your payment was received and each vendor has been notified. Digital downloads are available immediately in your order history.
        </p>
        <div className="co-order-ref">
          <strong>Order reference</strong>
          {orderNumber || orderId}
        </div>
        <button
          style={{
            width: "100%", padding: "13px", borderRadius: 12,
            background: "#1a4a3a", border: "none", color: "white",
            fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600,
            cursor: "pointer",
          }}
          onClick={onSuccess}
          data-testid="checkout-view-orders-btn"
        >
          View order history
        </button>
        <button
          style={{
            marginTop: 10, background: "none", border: "none", color: "#6a6058",
            fontFamily: "'DM Sans', sans-serif", fontSize: 13, cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={onDismiss}
          data-testid="checkout-keep-shopping-btn"
        >
          Keep shopping
        </button>
      </div>
    );
  }

  return (
    <div className="cart-checkout-panel">
      <div className="cart-checkout-header">
        <button className="cart-back-btn" onClick={onBack}>
          <ChevronRight size={20} style={{ transform: "rotate(180deg)" }} />
        </button>
        <h2 className="cart-checkout-title">Checkout</h2>
      </div>

      <div className="cart-checkout-body">
        {/* Order summary */}
        <div className="co-order-summary">
          {groupByVendor(cart.items).map(g => {
            const units = g.items.reduce((s, i) => s + (i.qty || 1), 0);
            return (
            <div key={g.vendor_id} className="co-order-row">
              <span>
                {g.vendor_name} ({units} item{units > 1 ? "s" : ""})
                {g.items.some(i => i.variant_name) && (
                  <span style={{ display: 'block', fontSize: 11.5, color: '#8a7a6a' }}>
                    {g.items.map(i => `${i.title}${i.variant_name ? ` · ${i.variant_name}` : ''}`
                      + (i.qty > 1 ? ` × ${i.qty}` : '')).join(', ')}
                  </span>
                )}
              </span>
              <span>${g.items.reduce((s, i) => s + i.price_snapshot * i.qty, 0).toFixed(2)}</span>
            </div>
            );
          })}
          {(intent?.amount_tax > 0 || intent?.amount_shipping > 0 || intent?.amount_processing > 0) && (
            <>
              <div className="co-order-row">
                <span>Subtotal</span>
                <span>${total.toFixed(2)}</span>
              </div>
              {intent?.amount_shipping > 0 && (
                <div className="co-order-row" data-testid="checkout-shipping-row">
                  <span>Shipping</span>
                  <span>${intent.amount_shipping.toFixed(2)}</span>
                </div>
              )}
              {intent?.amount_tax > 0 && (
                <div className="co-order-row" data-testid="checkout-sales-tax-row">
                  <span>Sales tax</span>
                  <span>${intent.amount_tax.toFixed(2)}</span>
                </div>
              )}
              {intent?.amount_processing > 0 && (
                <div className="co-order-row" data-testid="checkout-processing-row">
                  <span>Processing (3% + $0.30)</span>
                  <span>${intent.amount_processing.toFixed(2)}</span>
                </div>
              )}
            </>
          )}
          <div className="co-order-total">
            <span>Total</span>
            <span data-testid="checkout-total">${(intent?.amount_charged ?? total).toFixed(2)}</span>
          </div>
        </div>

        {/* Shipping address (only if physical items) */}
        {needsShipping && (
          <>
            <p className="co-section-title">Shipping address</p>
            <div className="co-field">
              <label className="co-label">Full name</label>
              <input className="co-input" placeholder="Maya Chen" value={address.name} onChange={e => upd({ name: e.target.value })} data-testid="co-ship-name" />
            </div>
            <div className="co-field">
              <label className="co-label">Address line 1</label>
              <input className="co-input" placeholder="123 Main Street" value={address.line1} onChange={e => upd({ line1: e.target.value })} data-testid="co-ship-line1" />
            </div>
            <div className="co-field">
              <label className="co-label">Address line 2 <span style={{ fontWeight: 400, color: "#a09080" }}>(optional)</span></label>
              <input className="co-input" placeholder="Apt, suite, unit..." value={address.line2} onChange={e => upd({ line2: e.target.value })} />
            </div>
            <div className="co-row">
              <div className="co-field">
                <label className="co-label">City</label>
                <input className="co-input" placeholder="Portland" value={address.city} onChange={e => upd({ city: e.target.value })} data-testid="co-ship-city" />
              </div>
              <div className="co-field" style={{ flex: "0 0 80px" }}>
                <label className="co-label">State</label>
                <input className="co-input" placeholder="OR" value={address.state} onChange={e => upd({ state: e.target.value })} data-testid="co-ship-state" />
              </div>
              <div className="co-field" style={{ flex: "0 0 90px" }}>
                <label className="co-label">ZIP</label>
                <input className="co-input" placeholder="97201" value={address.zip} onChange={e => upd({ zip: e.target.value })} data-testid="co-ship-zip" />
              </div>
            </div>
            <div className="co-divider" />
          </>
        )}

        {/* Billing ZIP — digital-only carts have no shipping address to source tax from */}
        {!needsShipping && taxEnabled && step !== "payment" && (
          <>
            <p className="co-section-title">Billing</p>
            <BillingZipField value={billingZip} onChange={setBillingZip} />
            <div className="co-divider" />
          </>
        )}

        {/* Payment */}
        <p className="co-section-title">Payment details</p>
        {step === "payment" && intent?.client_secret ? (
          <MarketplaceCardForm
            clientSecret={intent.client_secret}
            paymentIntentId={intent.payment_intent_id}
            orderId={intent.order_id}
            amount={intent.amount_charged ?? total}
            onSuccess={handlePaid}
          />
        ) : (
          <>
            <p style={{ fontSize: 12.5, color: "#8a7a6a", lineHeight: 1.55, margin: "0 0 16px" }}>
              We'll ask for your card on the next step. Your details go straight to Stripe —
              11/11 Collective Connection never stores them.
            </p>
            <button
              className="co-pay-btn"
              onClick={startPayment}
              disabled={processing}
              data-testid="continue-to-card-btn"
            >
              {processing
                ? <><Loader size={16} style={{ animation: "spin 1s linear infinite" }} /> Preparing…</>
                : <><Lock size={15} /> Continue to card · ${total.toFixed(2)}{taxEnabled ? " + tax" : ""}</>}
            </button>
          </>
        )}
        {error && (
          <p style={{ color: "#a02828", fontSize: 12.5, marginTop: 10 }} data-testid="checkout-error">{error}</p>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 11, color: "#a09080", marginTop: 14 }}>
          <Lock size={11} />
          Secured by Stripe · Each vendor gets their own receipt
        </div>
      </div>
    </div>
  );
}

// ─── Main CartDrawer ──────────────────────────────────────────────────────────

export default function CartDrawer({ onClose, onOrderSuccess, mode = "drawer", startInCheckout = false }) {
  const [cart, setCart] = useState({ items: [], updated_at: "" });
  // `mode="page"` renders the same cart and checkout inline for the /marketplace/cart route,
  // so there is one cart surface rather than a drawer and a separate page that drift apart.
  const [view, setView] = useState(startInCheckout ? "checkout" : "cart");
  const [discounts, setDiscounts] = useState({ per_vendor: [], applied_codes: {} });

  useEffect(() => {
    const load = () => { apiFetch("/cart").then(setCart).catch(() => {}); };
    load();
    // Anything that changes the cart elsewhere (Move to cart from Saved for later, the
    // added-to-cart overlay) fires this, or the cart body would keep showing stale lines.
    window.addEventListener("cart-updated", load);
    return () => window.removeEventListener("cart-updated", load);
  }, []);

  const refreshDiscounts = () => {
    apiFetch("/cart/discounts")
      .then(setDiscounts)
      .catch(() => setDiscounts({ per_vendor: [], applied_codes: {} }));
  };

  useEffect(() => { refreshDiscounts(); }, [cart]);

  const updateQty = async (item, newQty) => {
    // Two types of the same product are two lines — without the type, + on one raised the other
    // and the bin emptied both (testing agent, iteration 199).
    const variant = item.variant_name
      ? `?variant_name=${encodeURIComponent(item.variant_name)}` : "";
    try {
      if (newQty === 0) {
        await apiFetch(`/cart/items/${item.product_id}${variant}`, { method: "DELETE" });
      } else {
        await apiFetch(`/cart/items/${item.product_id}`, {
          method: "PATCH",
          body: JSON.stringify({ qty: newQty, variant_name: item.variant_name || null }),
        });
      }
      const updated = await apiFetch("/cart");
      setCart(updated);
      window.dispatchEvent(new Event("cart-updated"));
    } catch (e) { console.error(e); }
  };

  const removeCoupon = async (vendor_id) => {
    try {
      await apiFetch(`/cart/apply-coupon/${vendor_id}`, { method: "DELETE" });
      refreshDiscounts();
    } catch (e) { console.error(e); }
  };

  const removeItem = (item) => updateQty(item, 0);

  const itemCount = cart.items.reduce((s, i) => s + i.qty, 0);
  const vendorGroups = groupByVendor(cart.items);
  const total = cart.items.reduce((s, i) => s + i.price_snapshot * i.qty, 0);
  const totalSavings = (discounts.per_vendor || []).reduce((s, v) => s + (v.amount_off || 0), 0);
  const discountedTotal = Math.max(0, total - totalSavings);
  const hasPhysical = cart.items.some(i => i.type === "physical");
  const hasDigitalGoods = cart.items.some(i => i.type === "digital" || i.type === "course");

  // Shipping state
  const [shipAddr, setShipAddr] = useState({ street: "", city: "", state: "", zip_code: "" });
  const [shippingEstimates, setShippingEstimates] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingError, setShippingError] = useState("");
  const [showShipping, setShowShipping] = useState(false);

  const estimateShipping = async () => {
    if (!shipAddr.street || !shipAddr.city || !shipAddr.state || !shipAddr.zip_code) {
      setShippingError("All address fields are required");
      return;
    }
    setShippingLoading(true);
    setShippingError("");
    try {
      const data = await apiFetch("/marketplace/shipping/estimate", {
        method: "POST",
        body: JSON.stringify({ to_address: shipAddr }),
      });
      setShippingEstimates(data);
    } catch (e) {
      setShippingError(e?.message || "Could not estimate shipping. Please try again.");
    }
    setShippingLoading(false);
  };

  const shippingTotal = shippingEstimates?.total_cheapest_shipping || 0;

  const isPage = mode === "page";

  return (
    <>
      <style>{styles}</style>
      {!isPage && <div className="cart-overlay" onClick={onClose} />}
      <div className={isPage ? "cart-drawer is-page" : "cart-drawer"}>

        {view === "checkout" ? (
          <CheckoutPanel
            cart={cart}
            total={total}
            onBack={() => setView("cart")}
            onSuccess={() => {
              setCart({ ...cart, items: [] });
              setView("cart");
              // "View order history" used to call onOrderSuccess AND onClose, and the close
              // handler's navigation won — so it landed you back on the Marketplace home
              // instead of My Orders (owner, Jun 2026).
              if (onOrderSuccess) onOrderSuccess();
              else onClose?.();
            }}
            onDismiss={() => {
              setCart({ ...cart, items: [] });
              setView("cart");
              onClose?.();
            }}
          />
        ) : (
          <>
            {/* Cart header */}
            <div className="cart-header">
              <div className="cart-header-left">
                <ShoppingBag size={20} color="#1a4a3a" />
                <h2 className="cart-title">Your Cart</h2>
                {itemCount > 0 && <span className="cart-count">{itemCount}</span>}
              </div>
              {!isPage && <button className="cart-close" onClick={onClose}><X size={16} /></button>}
            </div>

            {/* Cart body */}
            <div className="cart-body">
              {cart.items.length === 0 ? (
                <div className="cart-empty">
                  <div className="cart-empty-icon"><ShoppingBag size={28} /></div>
                  <h3>Your cart is empty</h3>
                  <p>Browse the Marketplace to find something meaningful.</p>
                </div>
              ) : (
                vendorGroups.map(group => (
                  <div key={group.vendor_id} className="cart-vendor-group">
                    <div className="cart-vendor-header">
                      <div className="cart-vendor-avatar">{(group.vendor_name || "S")[0]}</div>
                      <span className="cart-vendor-name">{group.vendor_name || "Shop"}</span>
                      <span className="cart-vendor-subtotal">
                        ${group.items.reduce((s, i) => s + i.price_snapshot * i.qty, 0).toFixed(2)}
                      </span>
                    </div>
                    {group.items.map(item => (
                      <div key={`${item.product_id}::${item.variant_name || ''}`}
                        className="cart-item"
                        data-testid={`cart-line-${item.product_id}${vkey(item)}`}>
                        <div className="cart-item-thumb">
                          {item.image_url
                            ? <img src={item.image_url} alt={item.title}
                                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
                            : <span style={{ fontSize: 22 }}>{TYPE_ICONS[item.type]}</span>}
                        </div>
                        <div className="cart-item-info">
                          <div className="cart-item-type">{item.type}</div>
                          <p className="cart-item-name">{item.title}</p>
                          {item.variant_name && (
                            <p style={{ margin: '1px 0 0', fontSize: 11.5, color: '#8a7a6a' }}
                              data-testid={`cart-item-variant-${item.product_id}`}>
                              {item.variant_name}
                            </p>
                          )}
                          <div className="cart-item-controls">
                            {item.type === "digital" || item.type === "session" || item.type === "course" ? (
                              // Non-physical items: qty is always 1
                              <span style={{ fontSize: 12, color: "#8a7a6a" }}>Qty: 1</span>
                            ) : (
                              <div className="cart-qty-row">
                                <button className="cart-qty-btn" onClick={() => updateQty(item, item.qty - 1)} disabled={item.qty <= 1}
                                  data-testid={`cart-minus-${item.product_id}${vkey(item)}`}>
                                  <Minus size={12} />
                                </button>
                                <span className="cart-qty-val">{item.qty}</span>
                                <button className="cart-qty-btn" onClick={() => updateQty(item, item.qty + 1)}
                                  data-testid={`cart-plus-${item.product_id}${vkey(item)}`}>
                                  <Plus size={12} />
                                </button>
                              </div>
                            )}
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span className="cart-item-price">${(item.price_snapshot * item.qty).toFixed(2)}</span>
                              <button className="cart-remove-btn" onClick={() => removeItem(item)}
                                data-testid={`cart-remove-${item.product_id}${vkey(item)}`}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {cart.items.length > 0 && (
              <div className="cart-footer">
                <div className="cart-summary">
                  <div className="cart-summary-row">
                    <span>Subtotal ({itemCount} item{itemCount !== 1 ? "s" : ""})</span>
                    <span>${total.toFixed(2)}</span>
                  </div>

                  {/* Shipping Estimator */}
                  {hasPhysical && (
                    <div style={{ borderTop: "1px solid #e8e0d4", paddingTop: 12, marginTop: 8 }}>
                      {!showShipping && !shippingEstimates ? (
                        <button
                          onClick={() => setShowShipping(true)}
                          data-testid="estimate-shipping-btn"
                          style={{
                            display: "flex", alignItems: "center", gap: 6, width: "100%",
                            padding: "10px 14px", borderRadius: 8, border: "1px dashed #c4b8a8",
                            background: "rgba(45,107,74,0.04)", cursor: "pointer",
                            color: "#2d6b4a", fontSize: 13, fontWeight: 600,
                          }}
                        >
                          <Truck size={14} /> Estimate Shipping
                        </button>
                      ) : !shippingEstimates ? (
                        <div style={{ background: "#f5f0e8", borderRadius: 10, padding: 14 }}>
                          <p style={{ fontSize: 12, fontWeight: 700, color: "#5a4a3a", margin: "0 0 10px", display: "flex", alignItems: "center", gap: 4 }}>
                            <MapPin size={12} /> Delivery Address
                          </p>
                          <div style={{ display: "grid", gap: 8 }}>
                            <input
                              placeholder="Street address"
                              value={shipAddr.street}
                              onChange={e => setShipAddr(p => ({ ...p, street: e.target.value }))}
                              data-testid="ship-to-street"
                              style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #d4c8b8", fontSize: 13, background: "#ffffff", color: "#000000" }}
                            />
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                              <input
                                placeholder="City"
                                value={shipAddr.city}
                                onChange={e => setShipAddr(p => ({ ...p, city: e.target.value }))}
                                data-testid="ship-to-city"
                                style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #d4c8b8", fontSize: 13, background: "#ffffff", color: "#000000" }}
                              />
                              <input
                                placeholder="State (e.g. CA)"
                                value={shipAddr.state}
                                onChange={e => setShipAddr(p => ({ ...p, state: e.target.value.toUpperCase().slice(0, 2) }))}
                                data-testid="ship-to-state"
                                style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #d4c8b8", fontSize: 13, background: "#ffffff", color: "#000000" }}
                              />
                            </div>
                            <input
                              placeholder="ZIP code"
                              value={shipAddr.zip_code}
                              onChange={e => setShipAddr(p => ({ ...p, zip_code: e.target.value }))}
                              data-testid="ship-to-zip"
                              style={{ width: "50%", padding: "8px 12px", borderRadius: 6, border: "1px solid #d4c8b8", fontSize: 13, background: "#ffffff", color: "#000000" }}
                            />
                          </div>
                          {shippingError && <p style={{ color: "#c44", fontSize: 12, margin: "8px 0 0" }}>{shippingError}</p>}
                          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                            <button
                              onClick={estimateShipping}
                              disabled={shippingLoading}
                              data-testid="calculate-shipping-btn"
                              style={{
                                padding: "8px 16px", borderRadius: 6, border: "none",
                                background: "#2d6b4a", color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer",
                              }}
                            >
                              {shippingLoading ? "Calculating..." : "Calculate"}
                            </button>
                            <button
                              onClick={() => { setShowShipping(false); setShippingError(""); }}
                              style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #d4c8b8", background: "white", fontSize: 12, cursor: "pointer", color: "#8a7a6a" }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ background: "#f0f7f4", borderRadius: 10, padding: 14 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                            <p style={{ fontSize: 12, fontWeight: 700, color: "#2d6b4a", margin: 0, display: "flex", alignItems: "center", gap: 4 }}>
                              <Truck size={12} /> Shipping Estimate
                            </p>
                            <button
                              onClick={() => { setShippingEstimates(null); setShowShipping(true); }}
                              style={{ fontSize: 11, color: "#2d6b4a", textDecoration: "underline", background: "none", border: "none", cursor: "pointer" }}
                            >
                              Change address
                            </button>
                          </div>
                          {shippingEstimates.estimates?.map(e => (
                            <div key={e.vendor_id} style={{ marginBottom: 6 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#5a4a3a" }}>
                                <span>{e.store_name}</span>
                                {e.cheapest ? (
                                  <span style={{ fontWeight: 600 }}>
                                    ${parseFloat(e.cheapest.rate).toFixed(2)}
                                    <span style={{ fontWeight: 400, color: "#8a7a6a", marginLeft: 4, fontSize: 10 }}>
                                      {e.cheapest.carrier} {e.cheapest.delivery_days ? `· ${e.cheapest.delivery_days}d` : ""}
                                    </span>
                                  </span>
                                ) : (
                                  <span style={{ color: "#c44", fontSize: 11 }}>{e.error || "No rates"}</span>
                                )}
                              </div>
                            </div>
                          ))}
                          <div style={{ borderTop: "1px solid #d4e8de", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, color: "#2d6b4a" }}>
                            <span>Shipping total</span>
                            <span>${shippingTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="cart-summary-row">
                    <span>{vendorGroups.length} vendor{vendorGroups.length !== 1 ? "s" : ""}</span>
                    <span style={{ fontSize: 12, color: "#a09080" }}>Paid out separately</span>
                  </div>
                  {(discounts.per_vendor || []).filter(v => v.amount_off > 0 || v.free_shipping).map(v => (
                    <div key={v.vendor_id} className="cart-summary-row" data-testid={`cart-discount-${v.vendor_id}`}
                         style={{ color: '#1a5a44', fontSize: 12.5, fontWeight: 600 }}>
                      <span>{v.applied?.label || 'Discount'}{v.free_shipping ? ' + Free Shipping' : ''}{v.code ? <button onClick={() => removeCoupon(v.vendor_id)} style={{ marginLeft: 6, background: 'none', border: 'none', color: '#8a1f1f', cursor: 'pointer', fontSize: 11 }}>Remove</button> : null}</span>
                      <span>−${(v.amount_off || 0).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="cart-summary-total">
                    <span className="cart-summary-total-label">Total</span>
                    <span>${(discountedTotal + shippingTotal).toFixed(2)}</span>
                  </div>
                </div>
                {isMobileBuild() && hasDigitalGoods ? (
                  <div style={{ padding: "0 20px 20px" }} data-testid="cart-digital-web-notice-wrap">
                    <UpgradeOnWebNotice
                      title="Finish digital items on our website"
                      description="Your cart has digital items, which are purchased securely on our website. Complete your order there, then enjoy everything right here in the app."
                      ctaLabel="Continue on Website"
                      path="/dashboard/marketplace"
                      testId="cart-digital-web-notice"
                    />
                  </div>
                ) : (
                  <>
                    <button className="cart-checkout-btn" onClick={() => setView("checkout")} data-testid="cart-checkout-btn">
                      <Lock size={15} /> Proceed to checkout <ChevronRight size={16} />
                    </button>
                    <div className="cart-secure-note">
                      <Lock size={11} />
                      Secured by Stripe — your payment info is encrypted
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
